import React, { memo, useMemo } from 'react';
import { formatRelativeDate } from '../utils/dateUtils';
import { formatDurationRange } from '../../../../../../utils/taskDuration';
import { getTaskEstimatedDurationRange } from '../../../../FlowchartView/utils/progressUtils';

/**
 * MetaBadges - Displays metadata badges for a todo item
 * Shows: Inputs count, Deliverables count, Estimated time, Dates
 */
const MetaBadges = memo(({
  task,
  elementVisibility = {},
  statuses = [],
  nestedSprints = [],
  allSprints = [],
}) => {
  const estRange = useMemo(
    () => getTaskEstimatedDurationRange(task, { statuses, nestedSprints, allSprints }),
    [task, statuses, nestedSprints, allSprints],
  );
  const estLabel = estRange.maxMinutes != null
    ? formatDurationRange(estRange.minMinutes ?? 0, estRange.maxMinutes)
    : '';
  const hasEstRange = task.nestedSprintId
    && estRange.maxMinutes != null
    && (estRange.minMinutes ?? 0) !== estRange.maxMinutes;
  const estTitle = !estLabel
    ? undefined
    : hasEstRange
      ? `Fastest to longest route: ${estLabel}`
      : task.nestedSprintId
        ? `Estimated (nested total): ${estLabel}`
        : `Estimated: ${estLabel}`;
  return (
    <>
      {/* Inputs count */}
      {elementVisibility.inputs !== false && task.inputs && task.inputs.length > 0 && (
        <span className="todo-item__meta-badge todo-item__meta-badge--inputs" title={`${task.inputs.length} input(s)`}>
          <span className="todo-item__meta-label">Inputs</span>
          <span className="todo-item__meta-value">{task.inputs.length}</span>
        </span>
      )}

      {/* Deliverables count */}
      {elementVisibility.deliverables !== false && task.deliverables && task.deliverables.length > 0 && (
        <span className="todo-item__meta-badge todo-item__meta-badge--deliverables" title={`${task.deliverables.length} deliverable(s)`}>
          <span className="todo-item__meta-label">Outputs</span>
          <span className="todo-item__meta-value">{task.deliverables.length}</span>
        </span>
      )}

      {/* Estimated duration */}
      {elementVisibility.estimatedDurationMinutes !== false && estLabel && (
        <span className="todo-item__meta-badge todo-item__meta-badge--estimate" title={estTitle}>
          <span className="todo-item__meta-label">Est.</span>
          <span className="todo-item__meta-value">{estLabel}</span>
        </span>
      )}

      {/* Created date */}
      {elementVisibility.createdAt !== false && task.createdAt && (
        <span className="todo-item__meta-badge todo-item__meta-badge--date" title={`Created: ${new Date(task.createdAt).toLocaleDateString()}`}>
          <span className="todo-item__meta-label">Created</span>
          <span className="todo-item__meta-value">{formatRelativeDate(task.createdAt)}</span>
        </span>
      )}

      {/* Started date */}
      {elementVisibility.startedAt !== false && task.startedAt && (
        <span className="todo-item__meta-badge todo-item__meta-badge--started" title={`Started: ${new Date(task.startedAt).toLocaleDateString()}`}>
          <span className="todo-item__meta-label">Started</span>
          <span className="todo-item__meta-value">{formatRelativeDate(task.startedAt)}</span>
        </span>
      )}

      {/* Completed date */}
      {elementVisibility.completedAt !== false && task.completedAt && (
        <span className="todo-item__meta-badge todo-item__meta-badge--completed" title={`Completed: ${new Date(task.completedAt).toLocaleDateString()}`}>
          <span className="todo-item__meta-label">Done</span>
          <span className="todo-item__meta-value">{formatRelativeDate(task.completedAt)}</span>
        </span>
      )}

      {/* Updated date */}
      {elementVisibility.updatedAt !== false && task.updatedAt && (
        <span className="todo-item__meta-badge todo-item__meta-badge--updated" title={`Updated: ${new Date(task.updatedAt).toLocaleDateString()}`}>
          <span className="todo-item__meta-label">Updated</span>
          <span className="todo-item__meta-value">{formatRelativeDate(task.updatedAt)}</span>
        </span>
      )}
    </>
  );
});

MetaBadges.displayName = 'MetaBadges';

export default MetaBadges;
